import { cn } from "@/lib/utils";

/**
 * A tracked-object code label in IBM Plex Mono. Facts F-0042, stances S-0007, ideas I-0021,
 * history H-0118, week marker W29. These make list items read as tracked cargo.
 * Zero-pad numeric ids to 4 digits with `code(prefix, id)`. The week marker uses the `accent`
 * tone (cargo orange) per the scarcity rule; everything else is steel.
 * See .flow/DESIGN-SPEC.md § signature elements.
 */
export interface CodeLabelProps {
  children: React.ReactNode;
  /** steel (default muted code) | ink (stronger) | accent (cargo — week marker only) */
  tone?: "steel" | "ink" | "accent";
  className?: string;
}

const TONE: Record<NonNullable<CodeLabelProps["tone"]>, string> = {
  steel: "text-steel-dark",
  ink: "text-ink",
  accent: "text-cargo",
};

export function CodeLabel({ children, tone = "steel", className }: CodeLabelProps) {
  return (
    <span
      className={cn(
        "font-mono tracking-wider tabular-nums text-[11px] leading-none",
        TONE[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Zero-pad a numeric id and prefix it: code("F", 42) -> "F-0042". */
export function code(prefix: string, id: number | string): string {
  const n = typeof id === "number" ? id : parseInt(String(id).replace(/\D/g, ""), 10) || 0;
  return `${prefix}-${String(n).padStart(4, "0")}`;
}

export default CodeLabel;
